import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  ViewChild
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroXCircle, heroXMark } from '@ng-icons/heroicons/outline';
import { MarkdownComponent, provideMarkdown } from 'ngx-markdown';
import { FileSystemService } from '../../services/file-system.service';
import { DataviewerComponent } from '../dataviewer/dataviewer.component';
import { EditorComponent } from '../editor/editor.component';
import { Node } from '../explorer/node';
import { ImageviewerComponent } from '../imageviewer/imageviewer.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.css'],
  imports: [
    CommonModule,
    EditorComponent,
    DataviewerComponent,
    ImageviewerComponent,
    NgIcon,
    MarkdownComponent,
  ],
  standalone: true,
  providers: [provideIcons({ heroXCircle, heroXMark }), provideMarkdown()],
})
export class ViewerComponent implements AfterViewInit {
  @ViewChild('tabsContainer') tabsContainer!: ElementRef;
  fileType: 'code' | 'md' | 'data' | 'image' | 'yaml' | 'json' | 'unknown' = 'unknown';

  // @Input() filePath: string | null = null;
  visibleNode = computed(() => this.fs.visibleNode());
  tabsNames = computed(() => this.fs.tabs());
  visibleNodeMDContent = computed(async () => {
    if (this.fileType === 'md') {
      const content = this.visibleNode().content;
      const newContent = await this.updateImageLinks(content as string);
      // console.log('newContent', newContent);
      return newContent;
    } else {
      return "";
    }
  });

  // raw - preview
  viewMode = "raw";

  constructor(private fs: FileSystemService, private router: Router) {
    // Use effect to watch the signal
    effect(() => {
      const node = this.fs.visibleNode();
      this.fileType = this.fs.getFileType(node.path);
      setTimeout(() => this.scrollActiveTabIntoView(), 0);
    });
  }

  ngAfterViewInit() {
    // Initial scroll to active tab
    this.scrollActiveTabIntoView();

    if (this.tabsNames().length > 0) {
    this.tabsContainer.nativeElement.addEventListener('wheel', (event: WheelEvent) => {
      event.preventDefault();
      const container = this.tabsContainer.nativeElement;
      container.scrollTo({
        left: container.scrollLeft + (event.deltaY * 3),
        behavior: 'smooth'
      });
    }, { passive: false });
    }
  }

  private scrollActiveTabIntoView() {
    if (!this.tabsContainer) return;

    const container = this.tabsContainer.nativeElement;
    const activeTab = container.querySelector('.active-tab');
    if (!activeTab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    // If tab is not fully visible on the left
    if (tabRect.left < containerRect.left) {
      container.scrollLeft -= (containerRect.left - tabRect.left);
    }
    // If tab is not fully visible on the right
    else if (tabRect.right > containerRect.right) {
      container.scrollLeft += (tabRect.right - containerRect.right);
    }
  }

  onclick(msg: Node) {
    // console.log("clicked on ", msg);
    this.fs.changeVisibleNode(msg.path);
  }

  closeTab(value: Node) {
    // console.log("removing ", value);
    this.fs.removeVisibleNode(value);
  }

  togglePreview() {
    this.viewMode = this.viewMode === "raw" ? "preview" : "raw";
    if (this.viewMode === "preview") {
      const content = this.visibleNode().content;
      if (!content) {
        console.error("No content to preview");
        return;
      }
    }
  }

  async viewer() {
    const node = this.visibleNode();
    const blob = await node.getBlob();
    if (blob) {
      const blobUrl = URL.createObjectURL(blob);
      this.router.navigate(['/usdm-viewer'], { queryParams: { blobUrl } });
    }
  }

  async updateImageLinks(content: string): Promise<string> {
    const MD_IMG_REGEX = /(!\[[^\]]*]\()(\s*)(\/workspace\/[^)\s]+)(\s*\))/gi;

    const matches = [...content.matchAll(MD_IMG_REGEX)];

    const replacements = await Promise.all(
      matches.map(async (match) => {
        const [fullMatch, open, ws, path, close] = match;

        const node = this.fs.rootNode.getNode(path);
        const blob = node ? await node.getBlob() : undefined;
        const url = blob ? URL.createObjectURL(blob) : path;

        return {
          original: fullMatch,
          replacement: `${open}${ws}${url}${close}`,
        };
      })
    );

    // Reconstruct the final content with all replacements
    let newContent = content;
    for (const { original, replacement } of replacements) {
      newContent = newContent.replace(original, replacement);
    }

    return newContent;
  }
}
